import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMonkeysLocaleMessages } from '../../provider/context';
import {
  BaseContextMenu,
  BaseContextMenuContent,
  BaseContextMenuItem,
  BaseContextMenuLabel,
  BaseContextMenuSeparator,
  BaseContextMenuTrigger,
} from '../base/BaseContextMenu';
import {
  BaseTable,
  BaseTableBody,
  BaseTableCell,
  BaseTableContainer,
  BaseTableEmpty,
  BaseTableHead,
  BaseTableHeader,
  BaseTableRow,
} from '../base/BaseTable';
import type {
  BaseAppearance,
  BaseTableColumnOrderChangeDetail,
  BaseTableColumnSizeChangeDetail,
  BaseTableColumnSizes,
  BaseTableSortDirection,
} from '../base/types';
import { cn, hasRenderableNode } from '../base/utils';

export interface InteractiveTableColumn {
  key: string;
  label?: ReactNode;
  description?: ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  sortDirection?: BaseTableSortDirection;
  sortLabel?: string;
  draggable?: boolean;
  resizable?: boolean;
  className?: string;
  headerClassName?: string;
  cellClassName?: string;
  wrap?: boolean;
  [key: string]: any;
}

export interface InteractiveTableClassNames {
  root?: string;
  header?: string;
  headerLeading?: string;
  headerTitle?: string;
  headerDescription?: string;
  headerActions?: string;
  scrollArea?: string;
  table?: string;
  tableHeader?: string;
  headerRow?: string;
  headerCell?: string;
  body?: string;
  row?: string;
  pinnedTopRow?: string;
  cell?: string;
  empty?: string;
}

export interface InteractiveTableRenderContext<Row = any> {
  row: Row;
  rowIndex: number;
  column: InteractiveTableColumn;
  columnIndex: number;
  isPinnedTopRow: boolean;
}

export interface InteractiveTableHeaderContext<Row = any> {
  column: InteractiveTableColumn;
  columnIndex: number;
  columns: InteractiveTableColumn[];
  rows: Row[];
}

export interface InteractiveTableRowState {
  disabled?: boolean;
  pending?: boolean;
  saving?: boolean;
  selected?: boolean;
}

export interface InteractiveTableHeaderProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  resetColumnSizesLabel?: ReactNode;
}

export interface InteractiveTableColumnMenuLabels {
  columnActions?: ReactNode;
  freezeColumn?: ReactNode;
  freezeEndColumn?: ReactNode;
  unfreezeColumn?: ReactNode;
  unfreezeEndColumn?: ReactNode;
  unfreezeAllColumns?: ReactNode;
  resetColumnWidth?: ReactNode;
  frozenColumnTitle?: string;
}

export interface InteractiveTableProps<Row = any, PinnedTopRow = Row> {
  rows?: Row[];
  columns?: InteractiveTableColumn[];
  pinnedTopRows?: PinnedTopRow[];
  getRowKey?: (row: Row, rowIndex: number) => string | number;
  getPinnedTopRowKey?: (row: PinnedTopRow, rowIndex: number) => string | number;
  renderCell?: (column: InteractiveTableColumn, row: Row, context: InteractiveTableRenderContext<Row>) => ReactNode;
  renderPinnedTopCell?: (
    column: InteractiveTableColumn,
    row: PinnedTopRow,
    context: InteractiveTableRenderContext<PinnedTopRow>,
  ) => ReactNode;
  renderColumnHeader?: (column: InteractiveTableColumn, context: InteractiveTableHeaderContext<Row>) => ReactNode;
  onRowClick?: (row: Row, rowIndex: number) => void;
  getRowState?: (row: Row, rowIndex: number) => InteractiveTableRowState | undefined;
  rowClassName?: string | ((row: Row, rowIndex: number) => string | undefined);
  pinnedTopRowClassName?: string | ((row: PinnedTopRow, rowIndex: number) => string | undefined);
  emptyState?: ReactNode;
  header?: InteractiveTableHeaderProps;
  showResetColumnSizes?: boolean;
  columnMenuLabels?: InteractiveTableColumnMenuLabels;
  columnStorageKey?: string;
  columnOrder?: string[];
  columnSizes?: BaseTableColumnSizes;
  defaultColumnSizes?: BaseTableColumnSizes;
  columnDragEnabled?: boolean;
  columnResizeEnabled?: boolean;
  frozenColumnCount?: number;
  frozenEndColumnCount?: number;
  tableMinWidth?: number;
  bodyMaxHeight?: number | string;
  appearance?: BaseAppearance;
  className?: string;
  tableClassName?: string;
  classNames?: InteractiveTableClassNames;
  style?: CSSProperties;
  onColumnOrderChange?: (nextOrder: string[], detail: BaseTableColumnOrderChangeDetail) => void;
  onColumnSizeChange?: (columnId: string, width: number, detail: BaseTableColumnSizeChangeDetail) => void;
  onResetColumnSizes?: (nextSizes: BaseTableColumnSizes) => void;
  onFrozenColumnCountChange?: (nextCount: number) => void;
  onFrozenEndColumnCountChange?: (nextCount: number) => void;
  onSort?: (columnKey: string, column: InteractiveTableColumn) => void;
}

export interface InteractiveTableEditableTextCellProps {
  value?: string | null;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  commitOnBlur?: boolean;
  className?: string;
  inputClassName?: string;
  onCommit?: (value: string) => void | Promise<void>;
}

export interface InteractiveTableReadonlyCellProps {
  children?: ReactNode;
  mono?: boolean;
  className?: string;
}

export interface InteractiveTableSelectOption<T extends string = string> {
  value: T;
  label: string;
}

export interface InteractiveTableSelectCellProps<T extends string = string> {
  value: T;
  options: Array<InteractiveTableSelectOption<T>>;
  disabled?: boolean;
  ariaLabel: string;
  children: ReactNode;
  className?: string;
  onChange?: (nextValue: T) => void;
}

function toCssSize(value: number | string | undefined) {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function defaultGetRowKey(row: any, rowIndex: number) {
  return row?.id ?? row?.key ?? rowIndex;
}

function getColumnDefaultSize(column: InteractiveTableColumn) {
  if (typeof column.width === 'number') return column.width;
  if (typeof column.minWidth === 'number') return column.minWidth;
  return 160;
}

function buildDefaultColumnSizes(columns: InteractiveTableColumn[], explicit?: BaseTableColumnSizes) {
  return columns.reduce<BaseTableColumnSizes>((acc, column) => {
    acc[column.key] = explicit?.[column.key] ?? getColumnDefaultSize(column);
    return acc;
  }, {});
}

function loadStoredColumnSizes(storageKey: string | undefined, defaults: BaseTableColumnSizes) {
  if (!storageKey || !isBrowser()) return defaults;

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
    return Object.keys(defaults).reduce<BaseTableColumnSizes>((acc, key) => {
      const value = Number(parsed?.[key]);
      acc[key] = Number.isFinite(value) ? value : defaults[key];
      return acc;
    }, {});
  } catch {
    return defaults;
  }
}

function persistColumnSizes(storageKey: string | undefined, sizes: BaseTableColumnSizes) {
  if (!storageKey || !isBrowser()) return;
  window.localStorage.setItem(storageKey, JSON.stringify(sizes));
}

function getFrozenColumnStorageKey(storageKey: string | undefined) {
  return storageKey ? `${storageKey}.frozenColumnCount` : undefined;
}

function getFrozenEndColumnStorageKey(storageKey: string | undefined) {
  return storageKey ? `${storageKey}.frozenEndColumnCount` : undefined;
}

function clampFrozenColumnCount(count: number, columnCount: number) {
  if (!Number.isFinite(count)) return 0;
  return Math.max(0, Math.min(Math.round(count), columnCount));
}

function normalizeFrozenColumnCounts(startCount: number, endCount: number, columnCount: number) {
  const normalizedStartCount = clampFrozenColumnCount(startCount, columnCount);
  const normalizedEndCount = clampFrozenColumnCount(endCount, Math.max(0, columnCount - normalizedStartCount));
  return {
    startCount: normalizedStartCount,
    endCount: normalizedEndCount,
  };
}

function loadStoredFrozenColumnCount(
  storageKey: string | undefined,
  defaultCount: number,
  columnCount: number,
) {
  const fallback = clampFrozenColumnCount(defaultCount, columnCount);
  const frozenStorageKey = getFrozenColumnStorageKey(storageKey);
  if (!frozenStorageKey || !isBrowser()) return fallback;

  try {
    const storedValue = window.localStorage.getItem(frozenStorageKey);
    if (storedValue === null) return fallback;
    const value = Number(storedValue);
    return Number.isFinite(value) ? clampFrozenColumnCount(value, columnCount) : fallback;
  } catch {
    return fallback;
  }
}

function persistFrozenColumnCount(storageKey: string | undefined, count: number) {
  const frozenStorageKey = getFrozenColumnStorageKey(storageKey);
  if (!frozenStorageKey || !isBrowser()) return;
  window.localStorage.setItem(frozenStorageKey, String(count));
}

function loadStoredFrozenEndColumnCount(
  storageKey: string | undefined,
  defaultCount: number,
  columnCount: number,
) {
  const fallback = clampFrozenColumnCount(defaultCount, columnCount);
  const frozenStorageKey = getFrozenEndColumnStorageKey(storageKey);
  if (!frozenStorageKey || !isBrowser()) return fallback;

  try {
    const storedValue = window.localStorage.getItem(frozenStorageKey);
    if (storedValue === null) return fallback;
    const value = Number(storedValue);
    return Number.isFinite(value) ? clampFrozenColumnCount(value, columnCount) : fallback;
  } catch {
    return fallback;
  }
}

function persistFrozenEndColumnCount(storageKey: string | undefined, count: number) {
  const frozenStorageKey = getFrozenEndColumnStorageKey(storageKey);
  if (!frozenStorageKey || !isBrowser()) return;
  window.localStorage.setItem(frozenStorageKey, String(count));
}

function normalizeColumnOrder(columnKeys: string[], columnOrder?: string[]) {
  const validColumnKeys = new Set(columnKeys);
  const orderedKeys = (columnOrder || []).filter((columnKey) => validColumnKeys.has(columnKey));
  const orderedKeySet = new Set(orderedKeys);
  const remainingKeys = columnKeys.filter((columnKey) => !orderedKeySet.has(columnKey));
  return [...orderedKeys, ...remainingKeys];
}

function getOrderedColumns(columns: InteractiveTableColumn[], columnOrder?: string[]) {
  const normalizedOrder = normalizeColumnOrder(columns.map((column) => column.key), columnOrder);
  const columnMap = new Map(columns.map((column) => [column.key, column]));
  return normalizedOrder.map((columnKey) => columnMap.get(columnKey)).filter(Boolean) as InteractiveTableColumn[];
}

function getColumnNumericWidth(column: InteractiveTableColumn, columnSizes: BaseTableColumnSizes) {
  const controlledWidth = columnSizes[column.key];
  if (typeof controlledWidth === 'number') return controlledWidth;
  return getColumnDefaultSize(column);
}

function buildFrozenOffsets(
  columns: InteractiveTableColumn[],
  columnSizes: BaseTableColumnSizes,
  frozenColumnCount: number,
) {
  const offsets = new Map<string, number>();
  let left = 0;
  columns.slice(0, Math.max(0, frozenColumnCount)).forEach((column) => {
    offsets.set(column.key, left);
    left += getColumnNumericWidth(column, columnSizes);
  });
  return offsets;
}

function buildFrozenEndOffsets(
  columns: InteractiveTableColumn[],
  columnSizes: BaseTableColumnSizes,
  frozenEndColumnCount: number,
) {
  const offsets = new Map<string, number>();
  let right = 0;
  columns.slice(Math.max(0, columns.length - frozenEndColumnCount)).reverse().forEach((column) => {
    offsets.set(column.key, right);
    right += getColumnNumericWidth(column, columnSizes);
  });
  return offsets;
}

function resolveRowClassName<Row>(
  rowClassName: string | ((row: Row, rowIndex: number) => string | undefined) | undefined,
  row: Row,
  rowIndex: number,
) {
  return typeof rowClassName === 'function' ? rowClassName(row, rowIndex) : rowClassName;
}

function renderDefaultCell<Row>(column: InteractiveTableColumn, row: Row) {
  const value = (row as any)?.[column.key];
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function InteractiveTableCellContent({
  children,
  wrap,
}: {
  children: ReactNode;
  wrap?: boolean;
}) {
  return (
    <div
      className={cn(
        'min-w-0 max-w-full overflow-auto overscroll-contain [scrollbar-width:thin]',
        wrap ? 'max-h-20 whitespace-pre-wrap break-words leading-5' : 'whitespace-nowrap',
      )}
    >
      {children}
    </div>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
    >
      <path d="M5.25 2.75h5.5" />
      <path d="M6.25 2.75v4L4.5 9.25h7L9.75 6.75v-4" />
      <path d="M8 9.25v4" />
    </svg>
  );
}

export function InteractiveTable<Row = any, PinnedTopRow = Row>({
  rows = [],
  columns = [],
  pinnedTopRows = [],
  getRowKey = defaultGetRowKey,
  getPinnedTopRowKey = defaultGetRowKey,
  renderCell,
  renderPinnedTopCell,
  renderColumnHeader,
  onRowClick,
  getRowState,
  rowClassName,
  pinnedTopRowClassName,
  emptyState = '—',
  columnMenuLabels,
  columnStorageKey,
  columnOrder,
  columnSizes,
  defaultColumnSizes,
  columnDragEnabled = false,
  columnResizeEnabled = true,
  frozenColumnCount = 0,
  frozenEndColumnCount = 0,
  tableMinWidth,
  bodyMaxHeight = 'min(60vh, 42rem)',
  appearance,
  className,
  tableClassName,
  classNames,
  style,
  onColumnOrderChange,
  onColumnSizeChange,
  onResetColumnSizes,
  onFrozenColumnCountChange,
  onFrozenEndColumnCountChange,
  onSort,
}: InteractiveTableProps<Row, PinnedTopRow>) {
  const internalLabels = useMonkeysLocaleMessages().workbench.interactiveTable;
  const columnKeySignature = columns.map((column) => column.key).join('\u0001');
  const defaultOrder = useMemo(
    () => (columnKeySignature ? columnKeySignature.split('\u0001') : []),
    [columnKeySignature],
  );
  const defaultSizes = useMemo(
    () => buildDefaultColumnSizes(columns, defaultColumnSizes),
    [columns, defaultColumnSizes],
  );
  const [internalColumnOrder, setInternalColumnOrder] = useState(defaultOrder);
  const [internalColumnSizes, setInternalColumnSizes] = useState<BaseTableColumnSizes>(() => (
    loadStoredColumnSizes(columnStorageKey, defaultSizes)
  ));
  const [internalFrozenColumnCount, setInternalFrozenColumnCount] = useState(() => (
    loadStoredFrozenColumnCount(columnStorageKey, frozenColumnCount, columns.length)
  ));
  const [internalFrozenEndColumnCount, setInternalFrozenEndColumnCount] = useState(() => (
    loadStoredFrozenEndColumnCount(columnStorageKey, frozenEndColumnCount, columns.length)
  ));

  useEffect(() => {
    setInternalColumnOrder((current) => normalizeColumnOrder(defaultOrder, current));
  }, [defaultOrder]);

  useEffect(() => {
    if (columnSizes) return;
    setInternalColumnSizes(loadStoredColumnSizes(columnStorageKey, defaultSizes));
  }, [columnSizes, columnStorageKey, defaultSizes]);

  useEffect(() => {
    setInternalFrozenColumnCount(loadStoredFrozenColumnCount(columnStorageKey, frozenColumnCount, columns.length));
  }, [columnStorageKey, columns.length, frozenColumnCount]);

  useEffect(() => {
    setInternalFrozenEndColumnCount(loadStoredFrozenEndColumnCount(columnStorageKey, frozenEndColumnCount, columns.length));
  }, [columnStorageKey, columns.length, frozenEndColumnCount]);

  const resolvedColumnOrder = columnOrder
    ? normalizeColumnOrder(defaultOrder, columnOrder)
    : internalColumnOrder;
  const resolvedColumnSizes = columnSizes ?? internalColumnSizes;
  const orderedColumns = getOrderedColumns(columns, resolvedColumnOrder);
  const {
    startCount: resolvedFrozenColumnCount,
    endCount: resolvedFrozenEndColumnCount,
  } = normalizeFrozenColumnCounts(internalFrozenColumnCount, internalFrozenEndColumnCount, orderedColumns.length);
  const frozenOffsets = buildFrozenOffsets(orderedColumns, resolvedColumnSizes, resolvedFrozenColumnCount);
  const frozenEndOffsets = buildFrozenEndOffsets(orderedColumns, resolvedColumnSizes, resolvedFrozenEndColumnCount);
  const resolvedMinWidth = tableMinWidth ?? Math.max(760, orderedColumns.reduce((total, column) => total + getColumnNumericWidth(column, resolvedColumnSizes), 0));
  const labels = {
    columnActions: columnMenuLabels?.columnActions ?? internalLabels.columnActions,
    freezeColumn: columnMenuLabels?.freezeColumn ?? internalLabels.freezeColumn,
    freezeEndColumn: columnMenuLabels?.freezeEndColumn ?? internalLabels.freezeEndColumn,
    unfreezeColumn: columnMenuLabels?.unfreezeColumn ?? internalLabels.unfreezeColumn,
    unfreezeEndColumn:
      columnMenuLabels?.unfreezeEndColumn ?? internalLabels.unfreezeEndColumn,
    unfreezeAllColumns:
      columnMenuLabels?.unfreezeAllColumns ?? internalLabels.unfreezeAllColumns,
    resetColumnWidth:
      columnMenuLabels?.resetColumnWidth ?? internalLabels.resetColumnWidth,
    frozenColumnTitle:
      columnMenuLabels?.frozenColumnTitle ?? internalLabels.frozenColumnTitle,
  };

  const handleColumnOrderChange = useCallback((nextOrder: string[], detail: BaseTableColumnOrderChangeDetail) => {
    const normalizedOrder = normalizeColumnOrder(defaultOrder, nextOrder);
    if (!columnOrder) {
      setInternalColumnOrder(normalizedOrder);
    }
    onColumnOrderChange?.(normalizedOrder, detail);
  }, [columnOrder, defaultOrder, onColumnOrderChange]);

  const handleColumnSizeChange = useCallback((columnId: string, width: number, detail: BaseTableColumnSizeChangeDetail) => {
    const column = columns.find((item) => item.key === columnId);
    const minWidth = column?.minWidth;
    const maxWidth = column?.maxWidth;
    const nextWidth = Math.max(
      typeof minWidth === 'number' ? minWidth : 0,
      Math.min(width, typeof maxWidth === 'number' ? maxWidth : Number.POSITIVE_INFINITY),
    );
    const nextSizes = {
      ...resolvedColumnSizes,
      [columnId]: nextWidth,
    };
    if (!columnSizes) {
      setInternalColumnSizes(nextSizes);
    }
    persistColumnSizes(columnStorageKey, nextSizes);
    onColumnSizeChange?.(columnId, nextWidth, detail);
  }, [columnSizes, columnStorageKey, columns, onColumnSizeChange, resolvedColumnSizes]);

  const handleResetColumnSize = useCallback((column: InteractiveTableColumn) => {
    const nextWidth = defaultSizes[column.key] ?? getColumnDefaultSize(column);
    const previousWidth = getColumnNumericWidth(column, resolvedColumnSizes);
    const nextSizes = {
      ...resolvedColumnSizes,
      [column.key]: nextWidth,
    };
    if (!columnSizes) {
      setInternalColumnSizes(nextSizes);
    }
    persistColumnSizes(columnStorageKey, nextSizes);
    onColumnSizeChange?.(column.key, nextWidth, {
      columnId: column.key,
      previousWidth,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
    });
    onResetColumnSizes?.(nextSizes);
  }, [columnSizes, columnStorageKey, defaultSizes, onColumnSizeChange, onResetColumnSizes, resolvedColumnSizes]);

  const setFrozenColumnCount = useCallback((nextCount: number) => {
    const normalizedCount = clampFrozenColumnCount(nextCount, Math.max(0, orderedColumns.length - resolvedFrozenEndColumnCount));
    setInternalFrozenColumnCount(normalizedCount);
    persistFrozenColumnCount(columnStorageKey, normalizedCount);
    onFrozenColumnCountChange?.(normalizedCount);
  }, [columnStorageKey, onFrozenColumnCountChange, orderedColumns.length, resolvedFrozenEndColumnCount]);

  const setFrozenEndColumnCount = useCallback((nextCount: number) => {
    const normalizedCount = clampFrozenColumnCount(nextCount, Math.max(0, orderedColumns.length - resolvedFrozenColumnCount));
    setInternalFrozenEndColumnCount(normalizedCount);
    persistFrozenEndColumnCount(columnStorageKey, normalizedCount);
    onFrozenEndColumnCountChange?.(normalizedCount);
  }, [columnStorageKey, onFrozenEndColumnCountChange, orderedColumns.length, resolvedFrozenColumnCount]);

  const getFrozenPosition = (columnIndex: number) => {
    if (columnIndex < resolvedFrozenColumnCount) return 'start';
    if (columnIndex >= orderedColumns.length - resolvedFrozenEndColumnCount) return 'end';
    return undefined;
  };

  const getFrozenClassName = (column: InteractiveTableColumn, columnIndex: number, isHeader = false) => {
    const position = getFrozenPosition(columnIndex);
    if (!position) return undefined;
    return cn(
      'sticky',
      position === 'start' ? 'shadow-[1px_0_0_0_hsl(var(--border)/0.45)]' : 'shadow-[-1px_0_0_0_hsl(var(--border)/0.45)]',
      isHeader ? 'bg-muted/60' : 'bg-inherit',
      isHeader ? 'z-40' : 'z-20',
      (columnIndex === 0 || columnIndex === orderedColumns.length - 1) && (isHeader ? 'z-50' : 'z-30'),
    );
  };

  const getFrozenStyle = (column: InteractiveTableColumn, columnIndex: number): CSSProperties | undefined => {
    const position = getFrozenPosition(columnIndex);
    if (position === 'start') return { left: frozenOffsets.get(column.key) ?? 0 };
    if (position === 'end') return { right: frozenEndOffsets.get(column.key) ?? 0 };
    return undefined;
  };

  return (
    <BaseTableContainer
      appearance={appearance}
      className={cn('flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border/60 bg-card', classNames?.root, className)}
      style={style}
    >
      <div
        className={cn('min-h-0 flex-1 overflow-auto', classNames?.scrollArea)}
        style={{ maxHeight: toCssSize(bodyMaxHeight) }}
      >
        <BaseTable
          appearance={appearance}
          columnOrder={resolvedColumnOrder}
          columnSizes={resolvedColumnSizes}
          columnDragEnabled={columnDragEnabled}
          columnResizeEnabled={columnResizeEnabled}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnSizeChange={handleColumnSizeChange}
          className={cn('w-full table-fixed text-left text-xs', classNames?.table, tableClassName)}
          style={{ minWidth: resolvedMinWidth }}
        >
          <colgroup>
            {orderedColumns.map((column) => (
              <col
                key={column.key}
                style={{
                  width: toCssSize(resolvedColumnSizes[column.key] ?? column.width),
                  minWidth: toCssSize(column.minWidth),
                }}
              />
            ))}
          </colgroup>
          <BaseTableHeader appearance={appearance} className={cn('border-b border-border/60 bg-muted/60 text-[11px] font-medium text-muted-foreground', classNames?.tableHeader)}>
            <BaseTableRow appearance={appearance} className={cn('hover:bg-transparent', classNames?.headerRow)}>
              {orderedColumns.map((column, columnIndex) => {
                const headerContent = renderColumnHeader
                  ? renderColumnHeader(column, { column, columnIndex, columns: orderedColumns, rows })
                  : column.label;
                const sortable = Boolean(column.sortable);
                const handleSort = sortable && onSort
                  ? () => onSort(column.key, column)
                  : undefined;
                const isFrozenStart = columnIndex < resolvedFrozenColumnCount;
                const isFrozenEnd = columnIndex >= orderedColumns.length - resolvedFrozenEndColumnCount;
                const isFrozen = isFrozenStart || isFrozenEnd;
                const isColumnAtDefaultWidth = getColumnNumericWidth(column, resolvedColumnSizes) === (defaultSizes[column.key] ?? getColumnDefaultSize(column));

                return (
                  <BaseContextMenu key={column.key} modal={false}>
                    <BaseContextMenuTrigger asChild appearance={appearance}>
                      <BaseTableHead
                        appearance={appearance}
                        columnId={column.key}
                        align={column.align}
                        label={headerContent}
                        description={column.description}
                        trailing={isFrozen ? (
                          <span title={labels.frozenColumnTitle} className="inline-flex h-5 w-5 items-center justify-center rounded text-primary">
                            <PinIcon className="h-3.5 w-3.5" />
                          </span>
                        ) : null}
                        sortable={sortable}
                        sortDirection={column.sortDirection}
                        sortLabel={column.sortLabel}
                        onSort={handleSort}
                        draggable={column.draggable}
                        resizable={column.resizable}
                        width={column.width}
                        minWidth={column.minWidth}
                        maxWidth={column.maxWidth}
                        className={cn(
                          'sticky top-0 h-9 whitespace-nowrap border-r border-border/50 bg-muted/60 px-2.5 py-2 backdrop-blur',
                          getFrozenClassName(column, columnIndex, true),
                          column.headerClassName,
                          column.className,
                          classNames?.headerCell,
                        )}
                        style={getFrozenStyle(column, columnIndex)}
                      />
                    </BaseContextMenuTrigger>
                    <BaseContextMenuContent appearance={appearance} alignOffset={-4} className="min-w-44">
                      <BaseContextMenuLabel appearance={appearance}>
                        {labels.columnActions}
                      </BaseContextMenuLabel>
                      {isFrozen ? (
                        <BaseContextMenuItem
                          appearance={appearance}
                          onSelect={() => {
                            if (isFrozenEnd) setFrozenEndColumnCount(orderedColumns.length - columnIndex - 1);
                            else setFrozenColumnCount(columnIndex);
                          }}
                        >
                          {isFrozenEnd ? labels.unfreezeEndColumn : labels.unfreezeColumn}
                        </BaseContextMenuItem>
                      ) : (
                        <BaseContextMenuItem
                          appearance={appearance}
                          onSelect={() => setFrozenColumnCount(columnIndex + 1)}
                        >
                          {labels.freezeColumn}
                        </BaseContextMenuItem>
                      )}
                      {!isFrozen ? (
                        <BaseContextMenuItem
                          appearance={appearance}
                          onSelect={() => setFrozenEndColumnCount(orderedColumns.length - columnIndex)}
                        >
                          {labels.freezeEndColumn}
                        </BaseContextMenuItem>
                      ) : null}
                      <BaseContextMenuItem
                        appearance={appearance}
                        disabled={resolvedFrozenColumnCount === 0 && resolvedFrozenEndColumnCount === 0}
                        onSelect={() => {
                          setFrozenColumnCount(0);
                          setFrozenEndColumnCount(0);
                        }}
                      >
                        {labels.unfreezeAllColumns}
                      </BaseContextMenuItem>
                      <BaseContextMenuSeparator appearance={appearance} />
                      <BaseContextMenuItem
                        appearance={appearance}
                        disabled={isColumnAtDefaultWidth}
                        onSelect={() => handleResetColumnSize(column)}
                      >
                        {labels.resetColumnWidth}
                      </BaseContextMenuItem>
                    </BaseContextMenuContent>
                  </BaseContextMenu>
                );
              })}
            </BaseTableRow>
          </BaseTableHeader>
          <BaseTableBody appearance={appearance} className={cn('divide-y divide-border/50', classNames?.body)}>
            {pinnedTopRows.map((row, rowIndex) => {
              const key = getPinnedTopRowKey(row, rowIndex);
              const resolvedRowClassName = resolveRowClassName(pinnedTopRowClassName, row, rowIndex);
              return (
                <BaseTableRow
                  appearance={appearance}
                  key={`pinned-${key}`}
                  className={cn('bg-card/95', classNames?.pinnedTopRow, resolvedRowClassName)}
                >
                  {orderedColumns.map((column, columnIndex) => (
                    <BaseTableCell
                      appearance={appearance}
                      key={`pinned-${key}-${column.key}`}
                      align={column.align}
                      className={cn(
                        'h-[52px] border-r border-border/50 px-2.5 py-1.5 align-middle',
                        column.wrap ? 'whitespace-normal' : 'whitespace-nowrap',
                        getFrozenClassName(column, columnIndex),
                        column.cellClassName,
                        classNames?.cell,
                      )}
                      style={getFrozenStyle(column, columnIndex)}
                    >
                      <InteractiveTableCellContent wrap={column.wrap}>
                        {renderPinnedTopCell
                          ? renderPinnedTopCell(column, row, { row, rowIndex, column, columnIndex, isPinnedTopRow: true })
                          : renderDefaultCell(column, row)}
                      </InteractiveTableCellContent>
                    </BaseTableCell>
                  ))}
                </BaseTableRow>
              );
            })}
            {rows.length ? rows.map((row, rowIndex) => {
              const key = getRowKey(row, rowIndex);
              const state = getRowState?.(row, rowIndex) || {};
              const resolvedRowClassName = resolveRowClassName(rowClassName, row, rowIndex);

              return (
                <BaseTableRow
                  appearance={appearance}
                  key={key}
                  interactive={Boolean(onRowClick) && !state.disabled}
                  selected={state.selected}
                  disabled={state.disabled}
                  onClick={() => {
                    if (!state.disabled) onRowClick?.(row, rowIndex);
                  }}
                  className={cn(
                    'min-h-[52px] bg-card transition-colors',
                    state.saving && 'bg-primary/5',
                    state.pending && 'opacity-80',
                    onRowClick && !state.disabled ? 'cursor-pointer hover:bg-muted/30' : 'hover:bg-muted/30',
                    classNames?.row,
                    resolvedRowClassName,
                  )}
                >
                  {orderedColumns.map((column, columnIndex) => (
                    <BaseTableCell
                      appearance={appearance}
                      key={`${key}-${column.key}`}
                      align={column.align}
                      className={cn(
                        'h-[52px] border-r border-border/50 px-2.5 py-1.5 align-middle',
                        column.wrap ? 'whitespace-normal' : 'whitespace-nowrap',
                        getFrozenClassName(column, columnIndex),
                        column.cellClassName,
                        classNames?.cell,
                      )}
                      style={getFrozenStyle(column, columnIndex)}
                    >
                      <InteractiveTableCellContent wrap={column.wrap}>
                        {renderCell
                          ? renderCell(column, row, { row, rowIndex, column, columnIndex, isPinnedTopRow: false })
                          : renderDefaultCell(column, row)}
                      </InteractiveTableCellContent>
                    </BaseTableCell>
                  ))}
                </BaseTableRow>
              );
            }) : pinnedTopRows.length ? null : (
              <BaseTableEmpty
                appearance={appearance}
                colSpan={Math.max(orderedColumns.length, 1)}
                className={classNames?.empty}
              >
                {emptyState}
              </BaseTableEmpty>
            )}
          </BaseTableBody>
        </BaseTable>
      </div>
    </BaseTableContainer>
  );
}

export function InteractiveTableEditableTextCell({
  value,
  placeholder,
  disabled,
  multiline = false,
  commitOnBlur = true,
  className,
  inputClassName,
  onCommit,
}: InteractiveTableEditableTextCellProps) {
  const initialValue = String(value || '');
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    setDraft(initialValue);
  }, [initialValue]);

  const commit = useCallback(async () => {
    const nextValue = String(draft || '').trim();
    const previousValue = String(initialValue || '').trim();
    if (nextValue === previousValue) return;
    try {
      await onCommit?.(nextValue);
    } catch {
      setDraft(initialValue);
    }
  }, [draft, initialValue, onCommit]);

  const controlClassName = cn(
    'w-full resize-none rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs text-foreground outline-none transition-colors',
    'placeholder:text-muted-foreground/70 hover:border-border/60 hover:bg-background focus:border-primary/45 focus:bg-background',
    multiline ? 'min-h-[42px] leading-5' : 'h-8',
    disabled && 'cursor-not-allowed text-muted-foreground hover:border-transparent hover:bg-transparent',
    inputClassName,
  );

  if (multiline) {
    return (
      <textarea
        className={cn(controlClassName, className)}
        disabled={disabled}
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          if (commitOnBlur) void commit();
        }}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') {
            setDraft(initialValue);
            event.currentTarget.blur();
          }
        }}
      />
    );
  }

  return (
    <input
      className={cn(controlClassName, className)}
      disabled={disabled}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (commitOnBlur) void commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          setDraft(initialValue);
          event.currentTarget.blur();
        }
      }}
    />
  );
}

export function InteractiveTableReadonlyCell({
  children,
  mono = false,
  className,
}: InteractiveTableReadonlyCellProps) {
  return (
    <div className={cn('min-w-0 text-xs text-muted-foreground', mono && 'font-mono', className)}>
      {hasRenderableNode(children) ? children : '—'}
    </div>
  );
}

export function InteractiveTableSelectCell<T extends string = string>({
  value,
  options,
  disabled,
  ariaLabel,
  children,
  className,
  onChange,
}: InteractiveTableSelectCellProps<T>) {
  return (
    <div className={cn('relative flex w-full items-center', className)}>
      {children}
      <select
        aria-label={ariaLabel}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange?.(event.target.value as T)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
